import icTrash01Red from 'assets/images/ic-trash-01-red.svg'
import { Alert } from 'components/commons/alert'
import Button from 'components/commons/button'
import ErrorMessage from 'components/commons/error-message'
import FormModal from 'components/commons/form-modal'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { API_DEVELOPMENT_SUMMARY } from 'services/apiDevelopmentSummary'
import { useFetch } from 'services/useFetch'
import { capitalize, uploadFileToS3 } from 'utils/utils'

export const UploadFileDevelopmentSummaryContainer = ({ fileType, show, onHide, onSuccess }) => {
    const [searchParams] = useSearchParams()
    const periodId = searchParams.get('periodId')
    const [files, setFiles] = useState([])
    const [errorMessage, setErrorMessage] = useState()

    const { fetching: getUrlDocument, isLoading: isLoadingGetUrlDocument } = useFetch(
        API_DEVELOPMENT_SUMMARY.URL_DOCUMENT,
    )
    const { fetching: uploadDocument, isLoading: isLoadingUploadDocument } = useFetch({
        ...API_DEVELOPMENT_SUMMARY.UPLOAD_DOCUMENT,
        initPathVariable: periodId,
    })

    const onHideModal = () => {
        setFiles([])
        onHide()
    }

    const onChange = ({ value, index }) => {
        const copyItems = [...(files || [])]
        if (!index?.toString()) {
            if (Array.isArray(value)) {
                copyItems.push(...value)
            } else {
                copyItems.push(value)
            }
        } else if (!value) {
            copyItems.splice(index, 1)
        } else {
            copyItems[index] = value
        }

        setFiles(copyItems)
    }

    const onUpload = async (e) => {
        const allowedExtensions = /(\.pdf)$/i
        const maxSize = 10
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return

        const invalidFiles = []
        const validFiles = []

        for (const file of files) {
            const invalidExt = allowedExtensions && !allowedExtensions.exec(file?.name)
            const invalidSize = maxSize && file?.size > maxSize * 1000000

            if (invalidExt || invalidSize) {
                invalidFiles.push({
                    name: file?.name,
                    reason: invalidExt
                        ? 'Format file is invalid'
                        : `File size is invalid, valid size are not greater than ${maxSize} MB`,
                })
            } else {
                const base64 = await new Promise((resolve) => {
                    const reader = new FileReader()
                    reader.onload = () => resolve(reader.result)
                    reader.readAsDataURL(file)
                })

                validFiles.push({
                    file,
                    base64,
                    name: file?.name,
                })
            }
        }

        e.target.value = null

        if (invalidFiles.length) {
            const list = invalidFiles.map((file) => `• ${file?.name} (${file?.reason})`).join('\n')
            Alert({
                icon: 'error',
                title: 'Failed',
                message: list,
            })
        }

        if (validFiles.length) {
            onChange({ value: validFiles })
        }
    }

    const uploadAttachments = async (documents) => {
        if (!documents.length) return []

        const existingDocuments = documents
            .filter((doc) => doc.url)
            .map((doc) => ({
                url: doc.url,
                name: doc.name,
            }))

        const newDocuments = documents.filter((doc) => !doc.url)
        let uploadedNewFiles = []

        if (newDocuments.length > 0) {
            const filenames = newDocuments.map((item) => item.name)
            const { data: s3Urls } = await getUrlDocument({ files: filenames })

            uploadedNewFiles = await Promise.all(
                s3Urls.map(async (s3Item, index) => {
                    const file = newDocuments[index]?.file
                    await uploadFileToS3(s3Item, file)
                    return {
                        name: filenames[index],
                        url: `${s3Item.url}${s3Item.fields.key}`,
                    }
                }),
            )
        }

        return [...existingDocuments, ...uploadedNewFiles]
    }

    const onSubmitForm = async () => {
        try {
            const data = await uploadAttachments(files)
            const fileUrls = data.map((item) => item.url)
            setFiles(data)
            await uploadDocument({ file_type: fileType, files: fileUrls })
            onHideModal()
            onSuccess()
        } catch (error) {
            if (error?.code === '400-AD005') {
                const errorMessage = error?.data?.errors
                errorMessage.map((item) => {
                    files[item?.index].error = true
                })
                setFiles([...files])
            }
        }
    }

    return (
        <FormModal
            contentClassName='overflow-scroll'
            show={show}
            title={`Upload File ${capitalize(fileType?.replace('_', ' '))}`}
            onHide={onHideModal}
            onSubmitForm={onSubmitForm}
            width={'max-w-[768px]'}
            isLoading={isLoadingGetUrlDocument || isLoadingUploadDocument}
        >
            <div className={'flex flex-col px-6 py-4 gap-3'}>
                <div className='flex flex-col gap-3'>
                    {files?.map((fileItem, index) => (
                        <div>
                            <div key={fileItem?.name} className='flex items-center w-full gap-2'>
                                <div className='flex flex-1 gap-2'>
                                    <div className='flex-1 truncate w-32'>{fileItem?.name}</div>
                                    <ErrorMessage message={fileItem?.error && 'Invalid file name'} />
                                </div>

                                <Button
                                    type='outline'
                                    icon={icTrash01Red}
                                    onClick={() => onChange({ value: null, index })}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className='flex flex-col gap-2 justify-center sticky bottom-0 bg-white w-full p-4'>
                    <input
                        id={'files'}
                        type={'file'}
                        multiple={true}
                        className={'hidden'}
                        accept='application/pdf'
                        onChange={onUpload}
                    />
                    <label className='avo__button outline cursor-pointer w-1/3 mx-auto' type='button' htmlFor={'files'}>
                        <div className='btn-content'>{'Upload File'}</div>
                    </label>
                    <p className='text-center'>
                        {`File name format: "{NIP}-{Employee Name}.pdf" or "{NIP} - {Employee Name}.pdf"`}
                    </p>
                </div>
            </div>
        </FormModal>
    )
}
